import { QueryClient } from '@tanstack/react-query';
import {
  ADMIN_AI_PREVIEW_LIST,
  AI_LOGS,
  AI_LOGS_V2,
  ADMIN_AI_QUESTIONS,
  AI_INITIAL_SUMMARY,
  FRIENDS,
  HELP_CHAT,
  INVITES,
  ADMIN_AI_SETTINGS,
  AI_QUESTIONS,
  ADMIN_AI_PREVIEW_FAMILY,
  ADMIN_FAMILY_DETAIL,
  ADMIN_AI_ASK_QUESTION,
  AI_EXPANDED_SUMMARY,
  MAX_SCENARIOS,
  ACTIVE_CAMPAIGN,
  ADMIN_AI_RECENTS,
  ADMIN_AI_HISTORY,
  ADMIN_AI_RECENTS_V2,
  ADMIN_AI_HISTORY_V2,
  TERMS_AND_CONDITIONS,
  PRICES,
  BECOME_A_HERO_PRICES,
  ME,
  FAMILY,
  CHAT_TOKEN,
  DOGS,
  GAME_CREDS,
  ADMIN_TRAINER_DETAIL,
  ADMIN_TRAINER_PENDING,
  SUBSCRIPTION,
  COACH_CHAT_SIDS,
  COACH_CLIENT_DETAILS,
  COACH_PENDING_CLIENT_DETAILS
} from './queryKeys';

import {
  getAISettings,
  getAIQuestions,
  getPreviewList,
  getFamilyPreview,
  getAIRecents,
  getAIHistory,
  getTrainerDetail,
  getAIRecentsV2,
  getAIHistoryV2,
} from '../api/admin';
import { getFamily as getFamilyForAdmin, getPendingForCoach } from '../api/family';
import { 
  getActiveCampaign, 
  getHelpChat, 
  getMaxScenarios, 
  loadChatToken, 
  loadDogs, 
  loadGameCredentials, 
  loadSelf,
  getFamily,
} from '../api/me';
import { getAILogs, getQuestions, getAIExpandedSummary, getAIInitialSummaryv2 } from "../api/ai";
import { getTerms } from '../api/login';
import { getPrices, getBecomeAHeroPrices } from '../api/purchase';
import { getSubscriptionInfo } from '../api/dashboard';
import { initChatWithAllClients } from '../api/chat';
import { getClientsDetails, getPendingClientsDetails } from '../api/coach';

const queryClient = new QueryClient();

queryClient.setQueryDefaults(FRIENDS, {
  staleTime: 1000,
});

queryClient.setQueryDefaults(INVITES, {
  staleTime: 1000,
});

queryClient.setQueryDefaults(ADMIN_AI_PREVIEW_LIST, {
  queryFn: ({ queryKey }) => {
    const random = queryKey[1];
    if (random) return getPreviewList(true);
    return getPreviewList(null);
  },
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(ADMIN_AI_PREVIEW_FAMILY, {
  queryFn: ({ queryKey }) => getFamilyPreview(queryKey[1]),
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(ADMIN_FAMILY_DETAIL, {
  queryFn: ({ queryKey }) => getFamilyForAdmin(queryKey[1]),
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(HELP_CHAT, {
  queryFn: getHelpChat,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  retry: false,
});

queryClient.setQueryDefaults(ACTIVE_CAMPAIGN, {
  queryFn: getActiveCampaign,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  retry: false,
});

queryClient.setQueryDefaults(AI_INITIAL_SUMMARY, {
  queryFn: getAIInitialSummaryv2,
  staleTime: 30000,
  refetchOnWindowFocus: false,
  retry: false,
});
queryClient.setQueryDefaults(AI_EXPANDED_SUMMARY, {
  queryFn: getAIExpandedSummary,
  staleTime: 30000,
  refetchOnWindowFocus: false,
  retry: false,
});

queryClient.setQueryDefaults(AI_LOGS, {
  queryFn: getAILogs,
  staleTime: 5000,
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(AI_LOGS_V2, {
  staleTime: 5000,
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(AI_QUESTIONS, {
  queryFn: getQuestions,
  staleTime: 10000,
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(ADMIN_AI_SETTINGS, {
  queryFn: getAISettings,
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(ADMIN_AI_QUESTIONS, {
  queryFn: getAIQuestions,
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(ADMIN_AI_ASK_QUESTION, {
  refetchOnWindowFocus: false,
  staleTime: 10000,
  retry: false,
});

queryClient.setQueryDefaults(MAX_SCENARIOS, {
  queryFn: getMaxScenarios,
  refetchOnWindowFocus: false,
  staleTime: Infinity,
});

queryClient.setQueryDefaults(ADMIN_AI_RECENTS, {
  queryFn: getAIRecents,
  refetchOnWindowFocus: false,
  staleTime: 5000,
});

queryClient.setQueryDefaults(ADMIN_AI_HISTORY, {
  queryFn: ({ queryKey }) => getAIHistory(queryKey[1]),
  refetchOnWindowFocus: false,
  staleTime: 5000,
});

queryClient.setQueryDefaults(ADMIN_AI_RECENTS_V2, {
  queryFn: getAIRecentsV2,
  refetchOnWindowFocus: false,
  staleTime: 5000,
});

queryClient.setQueryDefaults(ADMIN_AI_HISTORY_V2, {
  queryFn: ({ queryKey }) => getAIHistoryV2(queryKey[1]),
  refetchOnWindowFocus: false,
  staleTime: 5000,
});

queryClient.setQueryDefaults(TERMS_AND_CONDITIONS, {
  queryFn: getTerms,
  refetchOnWindowFocus: false,
  staleTime: Infinity,
});

queryClient.setQueryDefaults(PRICES, {
  queryFn: ({ queryKey }) => getPrices(queryKey[1]?.couponId, queryKey[1]?.role),
  refetchOnWindowFocus: false,
  staleTime: Infinity,
});

queryClient.setQueryDefaults(BECOME_A_HERO_PRICES, {
  queryFn: getBecomeAHeroPrices,
  refetchOnWindowFocus: false,
  staleTime: Infinity,
  retry: false,
});

queryClient.setQueryDefaults(ME, {
  queryFn: loadSelf,
  staleTime: 10000,
});

queryClient.setQueryDefaults(FAMILY, {
  queryFn: getFamily,
  refetchOnWindowFocus: false,
  staleTime: 10000,
});

queryClient.setQueryDefaults(DOGS, {
  queryFn: loadDogs,
  staleTime: 10000,
});

queryClient.setQueryDefaults(GAME_CREDS, {
  queryFn: loadGameCredentials,
  refetchOnWindowFocus: false,
  staleTime: Infinity,
});

queryClient.setQueryDefaults(CHAT_TOKEN, {
  queryFn: loadChatToken,
  staleTime: Infinity,
});

queryClient.setQueryDefaults(ADMIN_TRAINER_DETAIL, {
  queryFn: ({ queryKey }) => getTrainerDetail(queryKey[1]),
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(ADMIN_TRAINER_PENDING, {
  queryFn: ({ queryKey }) => getPendingForCoach(queryKey[1]),
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(SUBSCRIPTION, {
  queryFn: getSubscriptionInfo,
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults(COACH_CHAT_SIDS, {
  queryFn: initChatWithAllClients,
  refetchOnWindowFocus: false,
  staleTime: Infinity,
  retry: false,
});

queryClient.setQueryDefaults(COACH_CLIENT_DETAILS, {
  queryFn: ({ queryKey }) => getClientsDetails(queryKey[1]),
  refetchOnWindowFocus: false,
  staleTime: 5000,
});

queryClient.setQueryDefaults(COACH_PENDING_CLIENT_DETAILS, {
  queryFn: ({ queryKey }) => getPendingClientsDetails(queryKey[1]),
  refetchOnWindowFocus: false,
  staleTime: 5000,
});

export default queryClient;
